import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Notification } from '../types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearError: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchNotifications: async () => {
    set({ isLoading: true, error: null });
    try {
      // First check if we have a session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        set({ notifications: [], unreadCount: 0 });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        set({ notifications: [], unreadCount: 0 });
        return;
      }
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const notifications = data.map(n => ({
        id: n.id,
        type: n.type as 'info' | 'success' | 'warning' | 'error',
        message: n.message,
        read: n.read,
        createdAt: n.created_at
      }));

      set({ 
        notifications,
        unreadCount: notifications.filter(n => !n.read).length,
        error: null
      });
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      set({ error: error?.message || 'Failed to fetch notifications' });
    } finally {
      set({ isLoading: false });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return;
      }

      // First update the database
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Database error when marking notification as read:', error);
        throw error;
      }

      // Then update the local state
      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1 >= 0 ? state.unreadCount - 1 : 0,
        error: null
      }));
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      set({ error: error?.message || 'Failed to mark notification as read' });
    }
  },

  markAllAsRead: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }
      
      // First update the database
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Database error when marking all notifications as read:', error);
        throw error;
      }

      // Then update the local state
      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
        error: null
      }));
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      set({ error: error?.message || 'Failed to mark all notifications as read' });
    }
  },

  clearError: () => set({ error: null })
}));

// Setup realtime subscription when auth state changes
const setupRealtimeSubscription = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  supabase
    .channel('public:notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      },
      (payload) => {
        const notification = {
          id: payload.new.id,
          type: payload.new.type as 'info' | 'success' | 'warning' | 'error',
          message: payload.new.message,
          read: payload.new.read,
          createdAt: payload.new.created_at
        };

        useNotificationStore.setState(state => ({
          notifications: [notification, ...state.notifications],
          unreadCount: state.unreadCount + 1
        }));
      }
    )
    .subscribe();
};

// Initialize by fetching notifications when auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN' && session) {
    useNotificationStore.getState().fetchNotifications();
    setupRealtimeSubscription();
  } else if (event === 'SIGNED_OUT') {
    useNotificationStore.setState({ notifications: [], unreadCount: 0 });
  }
});